import { getLocalTimeZone, today } from "@internationalized/date";
import { Effect, Record, Semaphore } from "effect";
import { Atom } from "effect/unstable/reactivity";
import {
  EMPTY_LESROOSTER,
  getChangedWeekdagen,
  getDagrooster,
  numberLesuren,
  type Lesrooster,
  type Lesuur,
} from "#/modules/les.planning/domain/lesrooster";
import {
  toRegelInput,
  type RoosterDocent,
  type Roosterregel,
  type RoosterregelInput,
} from "#/modules/les.planning/domain/rooster";
import * as Autosave from "#/modules/les.planning/services/Autosave";
import { PlanningApi } from "#/modules/les.planning/services/PlanningApi";
import { PlanningCache, type RoosterData } from "#/modules/les.planning/services/PlanningCache";
import { mutation, planningRuntime } from "#/modules/les.planning/services/runtime";
import { recordUndo } from "#/modules/les.planning/services/UndoHistory";
import { randomUuid } from "#/modules/util.helpers/utils.uuid";

export type RegelPlace = Pick<RoosterregelInput, "weekdag" | "lesuurId">;

/** Other klassen or another docent for a les of the rooster. */
export type RegelChange = Partial<
  Pick<Roosterregel, "klassen" | "schooldocentId" | "isCurrentUser">
>;

/**
 * What the docent just changed in the lessen of the rooster, shown until the refetch after the
 * save is in: changes by id, null for one that is being taken away.
 */
export const regelEditsAtom = Atom.make<Record<string, Partial<Roosterregel> | null>>({});

/** Lessen of the rooster being made, by a temporary id, until the refetch has them. */
export const newRegelsAtom = Atom.make<Record<string, Roosterregel>>({});

/** The lesrooster while a time is typed in; null once it is on its way. */
export const lesroosterDraftAtom = Atom.make<Lesrooster | null>(null);

const refreshRooster = PlanningCache.use((cache) => cache.refresh("rooster"));

/**
 * Saves of the lessen of the rooster go one at a time, in the order they were made: a klas and then
 * a docent picked quickly for one les would otherwise race, and the older save could land last.
 */
const regelSaves = Semaphore.makeUnsafe(1);

/** Shows `edit` on a regel while `save` runs, and lets it go once the fresh rooster is in. */
const withEdit = <A, E, R>(
  regelId: string,
  edit: Partial<Roosterregel> | null,
  save: Effect.Effect<A, E, R>,
) =>
  Effect.gen(function* () {
    // A change made while an earlier one still saves shows on top of it, until its own save is in.
    const pending = (yield* Atom.get(regelEditsAtom))[regelId];
    const shown = edit && pending ? { ...pending, ...edit } : edit;
    yield* Atom.update(regelEditsAtom, Record.set(regelId, shown));
    return yield* Semaphore.withPermit(regelSaves, save).pipe(
      Effect.ensuring(
        refreshRooster.pipe(
          Effect.andThen(
            Atom.update(regelEditsAtom, (edits) =>
              edits[regelId] === shown ? Record.remove(edits, regelId) : edits,
            ),
          ),
        ),
      ),
    );
  });

export const moveRegelAtom = mutation(
  Effect.fn("moveRegel")(function* ({ regel, to }: { regel: Roosterregel; to: RegelPlace }) {
    const api = yield* PlanningApi;
    const moveTo = (place: RegelPlace) =>
      withEdit(regel.id, place, api.updateRegel(regel.id, { ...toRegelInput(regel), ...place }));
    yield* moveTo(to);
    yield* recordUndo({
      undo: moveTo({ weekdag: regel.weekdag, lesuurId: regel.lesuurId }),
      redo: moveTo(to),
    });
  }),
);

export const changeRegelAtom = mutation(
  Effect.fn("changeRegel")(function* ({
    regel,
    change,
  }: {
    regel: Roosterregel;
    change: RegelChange;
  }) {
    const api = yield* PlanningApi;
    const save = (edit: RegelChange) =>
      withEdit(regel.id, edit, api.updateRegel(regel.id, toRegelInput({ ...regel, ...edit })));
    const previous: RegelChange = {
      klassen: regel.klassen,
      schooldocentId: regel.schooldocentId,
      isCurrentUser: regel.isCurrentUser,
    };
    yield* save(change);
    yield* recordUndo({ undo: save(previous), redo: save(change) });
  }),
);

/** A new les of the rooster in a lesuur. */
export interface NieuweRegel {
  readonly klassen: readonly { id: string; name: string }[];
  readonly lesuur: Lesuur;
  /** Who gives it; undefined is the docent who adds it. */
  readonly docent: RoosterDocent | undefined;
}

/** A new les of the rooster, valid from today: it shows at once and is one undo step. */
export const createRegelAtom = mutation(
  Effect.fn("createRegel")(function* ({ klassen, lesuur, docent }: NieuweRegel) {
    const api = yield* PlanningApi;
    const geldigVanaf = yield* Effect.sync(() => today(getLocalTimeZone()).toString());
    const preview: Roosterregel = {
      id: `new:${randomUuid()}`,
      klassen: [...klassen],
      schooldocentId: docent?.schooldocentId ?? "",
      isCurrentUser: docent?.isCurrentUser ?? true,
      weekdag: lesuur.weekdag,
      lesuurId: lesuur.id,
      startMinuten: lesuur.startMinuten,
      duurMinuten: lesuur.duurMinuten,
      geldigVanaf,
      geldigTot: null,
      uitzonderingen: [],
    };
    yield* Atom.update(newRegelsAtom, Record.set(preview.id, preview));
    const { ids } = yield* api
      .createRegels([
        {
          klasIds: klassen.map((klas) => klas.id),
          weekdag: lesuur.weekdag,
          lesuurId: lesuur.id,
          geldigVanaf,
          geldigTot: null,
          schooldocentId: docent?.schooldocentId ?? null,
        },
      ])
      .pipe(
        Effect.ensuring(
          refreshRooster.pipe(
            Effect.andThen(Atom.update(newRegelsAtom, Record.remove(preview.id))),
          ),
        ),
      );
    const each = (run: (id: string) => Effect.Effect<unknown, unknown>) =>
      Effect.forEach(ids, run, { concurrency: "unbounded" }).pipe(Effect.andThen(refreshRooster));
    yield* recordUndo({
      undo: each((id) => api.deleteRegel(id)),
      redo: each((id) => api.restoreRegel(id)),
    });
  }),
);

export const deleteRegelAtom = mutation(
  Effect.fn("deleteRegel")(function* (regel: Roosterregel) {
    const api = yield* PlanningApi;
    const remove = withEdit(regel.id, null, api.deleteRegel(regel.id));
    yield* remove;
    yield* recordUndo({
      undo: api.restoreRegel(regel.id).pipe(Effect.andThen(refreshRooster)),
      redo: remove,
    });
  }),
);

/** The rooster as it looks once a lesrooster is saved: its lessen follow their lesuur, or go with it. */
const withLesrooster = (data: RoosterData, lesrooster: Lesrooster): RoosterData => {
  const lesuren = new Map(numberLesuren(lesrooster.lesuren).map((lesuur) => [lesuur.id, lesuur]));
  return {
    ...data,
    lesrooster: { ...lesrooster, lesuren: [...lesuren.values()] },
    regels: data.regels.flatMap((regel) => {
      const lesuur = lesuren.get(regel.lesuurId);
      return lesuur
        ? [{ ...regel, startMinuten: lesuur.startMinuten, duurMinuten: lesuur.duurMinuten }]
        : [];
    }),
  };
};

const LESROOSTER_SAVE_DELAY = "500 millis";

/**
 * Lesrooster changes while the rooster page is open. A typed time fires per keystroke, so the
 * last value is saved once it rests, or right away when the page is left first. The backend saves
 * a weekdag at a time: every weekdag that changed is sent.
 */
const makeLesroosterSession = Effect.gen(function* () {
  const api = yield* PlanningApi;
  const cache = yield* PlanningCache;

  // The cache shows the new lesrooster from the moment the draft is let go: nothing jumps when the
  // server's answer takes over.
  const apply = (next: Lesrooster, isLast: Effect.Effect<boolean>) =>
    Effect.gen(function* () {
      const before = yield* cache.rooster;
      if (!before) return;
      const weekdagen = getChangedWeekdagen(before.lesrooster ?? EMPTY_LESROOSTER, next);
      yield* cache.setRooster(withLesrooster(before, next));
      yield* Atom.set(lesroosterDraftAtom, null);
      yield* Effect.forEach(
        weekdagen,
        (weekdag) => api.saveLesrooster(getDagrooster(next, weekdag)),
        { discard: true },
      ).pipe(
        Effect.catch(() => cache.setRooster(before)),
        // A refetch while a newer lesrooster is waiting would show the older one for a moment.
        Effect.andThen(
          Effect.flatMap(isLast, (last) =>
            last ? cache.refresh("rooster", "lesplanning") : Effect.void,
          ),
        ),
      );
    });

  const autosave: Autosave.Autosave<Lesrooster> = yield* Autosave.make({
    delay: LESROOSTER_SAVE_DELAY,
    save: (next: Lesrooster) =>
      Effect.gen(function* () {
        const before = yield* cache.rooster;
        const saved = before?.lesrooster ?? null;
        if (!before || (saved && getChangedWeekdagen(saved, next).length === 0)) {
          return yield* Atom.set(lesroosterDraftAtom, null);
        }
        // A lesuur that goes takes its lessen of the rooster along; undo brings them back as new ones.
        const lesuurIds = new Set(next.lesuren.map((lesuur) => lesuur.id));
        const weg = before.regels
          .filter((regel) => !lesuurIds.has(regel.lesuurId))
          .map(toRegelInput);
        yield* apply(
          next,
          Effect.map(autosave.hasWaiting, (waiting) => !waiting),
        );
        const restore =
          weg.length > 0
            ? api.createRegels(weg).pipe(Effect.andThen(cache.refresh("rooster")))
            : Effect.void;
        if (saved) {
          yield* recordUndo({
            undo: apply(saved, Effect.succeed(true)).pipe(Effect.andThen(restore)),
            redo: apply(next, Effect.succeed(true)),
          });
        }
      }),
  });

  return autosave;
});

// Kept for the whole page: a closed session would interrupt the next change instead of saving it.
const lesroosterSessionAtom = Atom.keepAlive(planningRuntime.atom(makeLesroosterSession));

/** A typed value: shown at once, saved once typing rests. */
export const changeLesroosterAtom = planningRuntime.fn((next: Lesrooster, get) =>
  Atom.set(lesroosterDraftAtom, next).pipe(
    Effect.andThen(get.result(lesroosterSessionAtom)),
    Effect.flatMap((session) => session.offer(next)),
  ),
);

/** A lesuur or pauze added or removed: saved right away. */
export const commitLesroosterAtom = planningRuntime.fn((next: Lesrooster, get) =>
  Effect.gen(function* () {
    yield* Atom.set(lesroosterDraftAtom, next);
    const session = yield* get.result(lesroosterSessionAtom);
    yield* session.offer(next);
    yield* session.flush;
  }).pipe(Effect.uninterruptible),
);

/** Saves what is still waiting, such as a time typed just before the page was left. */
export const flushLesroosterAtom = planningRuntime.fn((_: void, get) =>
  Effect.flatMap(get.result(lesroosterSessionAtom), (session) => session.flush).pipe(
    Effect.uninterruptible,
  ),
);
